/* import __COLOCATED_TEMPLATE__ from './change-project-price-modal.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import LSGModalBase from "../lsg/lsg-modal-dialog-base";
import { closeModal } from 'sh-www/utils/modal-controls';
import { PROJECT_LINE_ITEM } from "../../../models/project-line-item";
import { FEE_STRUCTURE } from 'sh-www/models/fee-structure';
import { setErrors } from 'sh-www/utils/handles-errors';

/**
 * This is the component for the modal to change a project's price
 */
const ChangeProjectPriceModal = LSGModalBase.extend({
  /**
   * Register services
   */
  errorTracker: service('error-tracker'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @param {Application|null}
     */
    application: null,

    /**
     * A callback with the new changed price after user confirms
     *
     * @param {Function}
     */
    onChangePriceConfirmed: () => {
      return (changedPrice) => {throw new Error('define change price confirm')};
    },

    /**
     * @param {String}      Placeholder for the text area the user will input.
     */
    placeholder: '0.00'
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {String}       This will hold the text value that the user will input.
     */
    newPrice: '',
  },

  /**
   * @inheritDoc
   */
  _onInitializationComplete() {
    this.set('state.newPrice', this.get('_currentBasePrice'));
    this.set('errors', {});
  },

  /**
   * Get the current base price of the application which is what the user would be changing.
   *
   * @type {Number}
   */
  _currentBasePrice: computed('model.application.projectLineItems.@each.amount', function() {
    let baseLineItem = this.get('model.application.projectLineItems').find(projectLineItem => {
      return projectLineItem.get('type') === PROJECT_LINE_ITEM.TYPES.BASE;
    });

    return baseLineItem ?
      baseLineItem.get('amount') :
      0;
  }),

  /**
   * @type {Number}
   */
  _newPriceFloat: computed('state.newPrice', function() {
    let changedValue = parseFloat(this.get('state.newPrice'));
    if (!changedValue) {
      return 0;
    }

    return changedValue;
  }),

  /**
   * Formatted string of the freelancer payout amount.
   *
   * @type {String}
   */
  _payoutFormatted: computed('_newPriceFloat', function() {
    return '$' + parseFloat(this.get('_newPriceFloat')).toFixed(2);
  }),

  /**
   * The service fee calculated and rounded as a float.
   *
   * @type {Float}
   */
  _serviceFeeFloat: computed(
    '_newPriceFloat',
    'model.application.feeStructure',
    'model.application.seller.hiringFeeStructure.buyerBaseFee',
    function () {
      const serviceFee = this.model.application.feeStructure?.[FEE_STRUCTURE.BUYER_FEES[FEE_STRUCTURE.FEE_TYPES.BASE_FEE]]
        ?? model.application.seller.hiringFeeStructure.buyerBaseFee;
      return serviceFee
        ? Math.round(this._newPriceFloat * serviceFee * 100) / 100
        : 0;
    },
  ),

  /**
   * The service fee formatted for the form.
   *
   * @type {String}
   */
  _serviceFeeFormatted : computed('_serviceFeeFloat', function() {
    return '$' + parseFloat(this.get('_serviceFeeFloat')).toFixed(2);
  }),

  /**
   * The total value (service fee + freelancer fee)
   *
   * @type {Float}
   */
  _total: computed('_newPriceFloat', '_serviceFeeFloat', function() {
    let newPriceFloat = this.get('_newPriceFloat');
    return newPriceFloat + this.get('_serviceFeeFloat');
  }),

  /**
   * The total formatted for the form.
   *
   * @type {String}
   */
  _totalFormatted: computed('_total', function() {
    return '$' + parseFloat(this.get('_total')).toFixed(2);
  }),

  actions: {
    /**
     * Called when the user confirms the price.
     */
    changePriceConfirmed: function() {
      let newPrice = this.get('_newPriceFloat'),
        currentPrice = parseFloat(this.get('model.application.price'));
      this.set('errors', {});

      if (!this.get('currentUser.isAdmin') && newPrice <= currentPrice) {
        setErrors(this, { 'client-exception': 'Please enter a price greater than the current project price.' }, ChangeProjectPriceModal.FORM_FIELDS);
        return;
      }

      closeModal(this);
      this.get('model.onChangePriceConfirmed')(newPrice);
    },

    onInputNewPrice(text) {
      this.set('state.newPrice', text);
    },

    closeModal() {
      closeModal(this);
    },
  }
});

ChangeProjectPriceModal.reopenClass({
  FORM_FIELDS: [
    'client-exception'
  ]
});

export default ChangeProjectPriceModal;
